import { CustomCellRendererProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { ReactElement } from 'react';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { AssetInteractionListModel } from '@customer/store/apis/interactionsApi';
import { IRowNode, ValueGetterParams } from 'ag-grid-community';
import { EmptyCell } from '../../formatters/genericFormatters';
import { DefaultCell } from '@dmc/ui/tables';

export type ActiveDurationModel = Pick<AssetInteractionListModel, 'act' | 'dur'>;

export type ActiveDurationValue = Partial<ActiveDurationModel>;

export const activeDurationValueGetter = <T extends ActiveDurationModel>({
  data,
}: ValueGetterParams<T, ActiveDurationValue>): ActiveDurationValue => ({
  act: data?.act,
  dur: data?.dur,
});

const getActiveOrder = (actA: boolean, actB: boolean): number => {
  let activeOrder = 0;

  if (actA) {
    if (!actB) {
      activeOrder = 1;
    }
  } else if (actB) {
    activeOrder = -1;
  }

  return activeOrder;
};

const getDurationOrder = (durA: number | null, durB: number | null): number => {
  if (durA === durB) return 0;
  if (durA === null) return -1;
  if (durB === null) return 1;
  return durA > durB ? 1 : -1;
};

export const activeDurationComparator = <T extends ActiveDurationModel>(
  valueA: unknown,
  valueB: unknown,
  nodeA: IRowNode<T>,
  nodeB: IRowNode<T>,
  isDescending: boolean,
): number => {
  if (typeof nodeA.data !== 'undefined' && typeof nodeB.data !== 'undefined') {
    const dataA = nodeA.data;
    const dataB = nodeB.data;

    if (dataA.dur === dataB.dur && dataA.act === dataB.act) return 0;

    const activeOrder = getActiveOrder(dataA.act, dataB.act);

    if (activeOrder !== 0) return activeOrder;

    return getDurationOrder(dataA.dur, dataB.dur);
  }

  return 0;
};

export function ActiveDurationRenderer({
  value,
  ...otherProps
}: CustomCellRendererProps<ActiveDurationModel, ActiveDurationValue>): ReactElement {
  const { t } = useTranslation();
  const { showDurationFromSeconds } = useLocaleFormatter();

  let text = EmptyCell;

  if (value) {
    if (value.act) {
      text = t('Active');
    } else if (typeof value.dur === 'number') {
      text = showDurationFromSeconds(value.dur);
    }
  }

  return <DefaultCell {...otherProps} value={text} />;
}

export default ActiveDurationRenderer;
